// @ts-check

import Vue from 'vue';
import { get, put } from '@/utils/api';
import { getThemeStyles, insertTheme, getBlankTheme, getBrandTheme } from '@/utils/designProcessors';

/**
 * Replaces dates with strings
 * @typedef {import('acb-lib').TypeUtils.ToFrontEnd<T>} ToFrontEnd
 * @template T
 */

/**
 * @typedef {import('acb-lib').Brands.Brand} Brand
 * @typedef {import('acb-lib').Brands.BrandSettings} BrandSettings
 * @typedef {import('acb-lib').Brands.BrandSettingKey} BrandSettingKey
 * @typedef {import('acb-lib').Brands.BrandImageType} BrandImageType
 * @typedef {import('acb-lib').Brands.GetThemeStyleEndpointResError} GetThemeStyleEndpointResError
 * @typedef {import('acb-lib').Brands.BrandThemeStylesByBrandId} BrandThemeStylesByBrandId
 * @typedef {import('acb-lib').Theme.ThemeStyle} ThemeStyle
 * @typedef {import('acb-lib').Theme.StyleRuleOptionsType} StyleRuleOptionsType
 *
 * @typedef {typeof state} State
 * @typedef {typeof getters} Getters
 */

const state = /** @type {const} */ ({
	/**
	 * @type {{
	 * 		[key: number]: BrandSettings
	 * }}
	 */
	brandSettingsByBrandId: {},
	/** @type {ToFrontEnd<Brand>[]} */
	brands: [],
	/**
	 * @type {Record<string, ThemeStyle[]>}
	 */
	themeStylesByBrandId: {},
	/** @type {number | undefined} */
	previewingBrandWithId: undefined,
	hasThemeStyles: false,
	blankThemeRequired: false
});

/**
 * @satisfies {{
* 		[key: string]: (state: State, ctx: any) => any;
* }}
*/
const mutations = {
	SET_BRAND_SETTINGS_BY_BRAND_ID(state, { brandId, brandSettings })
	{
		Vue.set(
			state.brandSettingsByBrandId,
			brandId,
			brandSettings
		);
	},
	SET_ALL_BRAND_SETTINGS_BY_BRAND_ID(state, { brandSettings })
	{
		Vue.set(state, 'brandSettingsByBrandId', brandSettings);
	},
	SET_BLANK_THEME_REQUIRED(state, required)
	{
		Vue.set(state, 'blankThemeRequired', required);
	},
	SET_ALL_BRAND_THEME_STYLES_BY_BRAND_ID(state, { styles })
	{
		Vue.set(state, 'themeStylesByBrandId', styles);
	},
	SET_BRAND_NAME(state, { brandId, brandName })
	{
		const brandIndex = state.brands
			.findIndex((brand) => brandId === brand.brandId);

		Vue.set(
			state.brands[brandIndex],
			'brandName',
			brandName
		);
	},
	SET_BRAND_SETTING(state, { brandId, brandSettingKey, brandSettingValue })
	{
		if(!state.brandSettingsByBrandId[brandId])
		{
			Vue.set(
				state.brandSettingsByBrandId,
				brandId,
				{}
			);
		}

		Vue.set(
			state.brandSettingsByBrandId[brandId],
			brandSettingKey,
			brandSettingValue
		);
	},
	SET_BRANDS(state, { brands })
	{
		Vue.set(state, 'brands', brands);
	},
	SET_BRAND(state, { brand })
	{
		Vue.set(
			state,
			'brands',
			[
				...state.brands.filter(({ brandId }) => brandId !== brand.brandId),
				brand
			]
		);
	},
	SET_THEME_STYLES_BY_BRAND_ID(state, { styles, brandId })
	{
		Vue.set(
			state,
			'themeStylesByBrandId',
			{
				...state.themeStylesByBrandId,
				[brandId]: styles
			}
		);
	},
	SET_PREVIEWING_BRAND_WITH_ID(state, brandId)
	{
		Vue.set(state, 'previewingBrandWithId', brandId);
	},
	RESET_PREVIEWING_BRAND_WITH_ID(state)
	{
		Vue.set(state, 'previewingBrandWithId', undefined);
	},
	SET_HAS_THEME_STYLES(state, hasThemeStyles)
	{
		Vue.set(state, 'hasThemeStyles', hasThemeStyles);
	}
};

/**
 * Yes, I put `any`: there's some type assurance, but not all.
 *
 * @satisfies {{
 * 		[key: string]: (ctx: {
 * 			state: State;
 * 			dispatch: any;
 * 			commit: any;
 * 			getters: Record<keyof Getters, any>;
 * 			rootGetters: any;
 * 		}, payload: any) => any;
 * }}
 */
const actions = {
	async loadAndSetBrands({ commit })
	{
		const { data } = await get('/service/brands');

		commit('SET_BRANDS', { brands: data });
	},
	async loadAndSetOneBrand({ commit, getters }, { brandId, force = false } = {})
	{
		if(getters.getBrandById(brandId) && !force)
		{
			return;
		}

		const { data: brand } = await get(`/service/brands/${brandId}`);

		commit('SET_BRAND', { brand, brandId });
	},
	setBrandSettings({ commit }, { brandId, brandSettings })
	{
		commit('SET_BRAND_SETTINGS_BY_BRAND_ID', { brandId, brandSettings });
	},
	async updateBrandName({ commit }, { brandId, brandName })
	{
		await put(
			`service/brands/${brandId}/name`,
			{ brandName }
		);

		commit('SET_BRAND_NAME', { brandId, brandName });
	},
	/**
	 * Custom CSS should not be visible on the admin dashboard.
	 */
	async setBlankThemeRequired({ commit, dispatch }, required)
	{
		commit('SET_BLANK_THEME_REQUIRED', required);

		if(required)
		{
			await dispatch('applyBlankTheme');

			return;
		}

		await dispatch('applyActiveBrandOrPreviewTheme');
	},
	setBrandSetting({ commit }, { brandId, brandSettingKey, brandSettingValue })
	{
		commit('SET_BRAND_SETTING', { brandId, brandSettingKey, brandSettingValue });
	},
	async loadBrandSettingsByBrandId({ commit }, { brandId })
	{
		const { data } = await get(`service/brands/${brandId}/settings`);

		commit('SET_BRAND_SETTINGS_BY_BRAND_ID', { brandId, brandSettings: data });

		return undefined;
	},
	async loadAllBrandThemeStylesByBrandId({ commit, state }, { force = false } = {})
	{
		if(state.hasThemeStyles && !force)
		{
			return state.themeStylesByBrandId;
		}

		const { data } = /** @type {{ data: BrandThemeStylesByBrandId }} */ (
			await get('service/brands/all/themeStyles')
		);

		commit('SET_ALL_BRAND_THEME_STYLES_BY_BRAND_ID', { styles: data });

		commit('SET_HAS_THEME_STYLES', true);

		return data;
	},
	async loadAllBrandSettingsByBrandId({ commit, state }, { force = false } = {})
	{
		const hasSettings = !!Object.keys(state.brandSettingsByBrandId).length;

		if(hasSettings && !force)
		{
			return;
		}

		const { data } = await get('service/brands/all/settings');

		commit('SET_ALL_BRAND_SETTINGS_BY_BRAND_ID', { brandSettings: data });
	},
	async applyBrandTheme({ dispatch, getters }, brandId)
	{
		if(getters.blankThemeRequired)
		{
			return { theme: '' };
		}

		await dispatch('loadBrandSettingsByBrandId', { brandId });

		await dispatch('loadAndSetThemeStylesByBrandId', brandId);

		const theme = await getBrandTheme({ brandId });

		insertTheme(theme);

		return { theme };
	},
	async applyBlankTheme()
	{
		insertTheme(await getBlankTheme());
	},
	async applyActiveBrandTheme({ dispatch, getters })
	{
		return dispatch('applyBrandTheme', getters.activeBrandId);
	},
	async applyActiveBrandOrPreviewTheme({ getters, dispatch })
	{
		if(getters.previewingBrandWithId)
		{
			return dispatch('applyBrandTheme', getters.previewingBrandWithId);
		}

		return dispatch('applyActiveBrandTheme');
	},
	async setPreviewingBrandId({ dispatch, commit }, brandId)
	{
		if(typeof brandId !== 'number')
		{
			throw new Error(`Received "${brandId}" as a brandId but it is not number.`);
		}

		commit('SET_PREVIEWING_BRAND_WITH_ID', brandId);

		await dispatch('applyBrandTheme', brandId);
	},
	async resetBrandPreview({ dispatch, getters, commit })
	{
		commit('RESET_PREVIEWING_BRAND_WITH_ID');

		await dispatch('applyBrandTheme', getters.activeBrandId);
	},
	async setActiveBrand({ dispatch }, { brandId })
	{
		await dispatch(
			'app/settings/changeSetting',
			{
				name: 'brand',
				changes: { id: brandId }
			},
			{ root: true }
		);

		await dispatch('applyBrandTheme', brandId);
	},
	async loadAndSetActiveBrandThemeStyles({ dispatch, getters })
	{
		await dispatch('loadAndSetThemeStylesByBrandId', getters.activeBrandId);
	},
	async loadAndSetThemeStylesByBrandId({ commit }, brandId)
	{
		const themeStylesRes = await getThemeStyles({ brandId });

		const { styles } = themeStylesRes;

		commit('SET_THEME_STYLES_BY_BRAND_ID', { styles, brandId });

		return undefined;
	},
	async setThemeStyles(
		{ dispatch, state },
		/** @type {{ brandId: number; changes: ThemeStyle[] }} */
		{ brandId, changes }
	)
	{
		const styles = state.themeStylesByBrandId[brandId];

		const modifiedStyles = styles.map((style) =>
		{
			const updatedStyle = changes.find((updated) => updated.rule === style.rule);

			return updatedStyle ? { ...style, ...updatedStyle } : style;
		});

		const result = await put(
			`service/brands/${brandId}/themeStyles`,
			{ styles: modifiedStyles }
		);

		// refresh everything
		await dispatch('loadAllBrandThemeStylesByBrandId', { force: true });

		return result;
	}
};

/**
 * @satisfies {{
 * 		[key: string]: (
 * 			state: State,
 * 			getters: any,
 * 			rootState: any,
 * 			rootGetters: any
 * 		) => any;
 * }}
 */
const getters = {
	activeBrandId: (state, getters, rootState, rootGetters) =>
	{
		if(state.previewingBrandWithId)
		{
			return state.previewingBrandWithId;
		}

		return rootGetters['app/settings/get']('brand.id');
	},
	activeBrandSettings: (state, getters) =>
	{
		return state.brandSettingsByBrandId[getters.activeBrandId];
	},
	blankThemeRequired: (state) =>
	{
		return state.blankThemeRequired;
	},
	all: (state) =>
	{
		return state.brands;
	},
	previewingBrandWithId: (state) =>
	{
		return state.previewingBrandWithId;
	},
	getBrandById: (state) => (/** @type {number} */ brandId) =>
	{
		return (state.brands)?.find((brand) => brand.brandId === brandId);
	},
	getBrandSettingsByBrandId: (state) => (/** @type {number} */ brandId) =>
	{
		return (state.brandSettingsByBrandId[brandId]);
	},
	themeStylesByBrandId: (state) => (/** @type {number} */ brandId) =>
	{
		return state.themeStylesByBrandId[brandId] || [];
	},
	/**
	 * @returns {(rules: StyleRuleOptionsType[]) => ThemeStyle[]}
	 */
	activeBrandThemeStylesByRules: (state, getters) => (rules) =>
	{
		return getters.themeStylesByRulesByBrandId(
			getters.activeBrandId
		)(rules);
	},
	/**
	 * @returns {(brandId: number) => (rules: StyleRuleOptionsType[]) => ThemeStyle[]}
	 */
	themeStylesByRulesByBrandId: (state) => (brandId) => (rules) =>
	{
		if(!state.themeStylesByBrandId[brandId])
		{
			return [];
		}

		return state.themeStylesByBrandId[brandId].reduce((acc, arg) =>
		{
			if(rules.find((style) => style === arg.rule))
			{
				acc.push(arg);
			}

			return acc;
		}, /** @type {ThemeStyle[]} */ ([]));
	}
};

export default {
	namespaced: true,
	state,
	mutations,
	actions,
	getters
};
